import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import HomepageStickerCard from './HomepageStickerCard';
import ParallaxComponent from './ParallaxComponent';
import MaterialIcon from './MaterialIcon';
import ClothedEyeLoader from './ClothedEyeLoader';
import ClothedEyeError from './ClothedEyeError';
import parallaxService from '../services/parallaxService';

import logger from '../utils/logger.js';
// Debug flag for this component
const DEBUG_STICKER_GRID = false;
const StickerGrid = React.memo(({ 
  listings, 
  loading, 
  error, 
  groupByCategory, 
  isModalOpen,
  modalImageLoaded = false, // Track when modal image is loaded to resume grid loading
  containerRef,
  onCardClick, 
  onAddToScene,
  showCategoryHeaders = true,
  categorySceneAssignments = {},
  onStickerClick,
  onSceneStickersUpdate,
  onSceneNameUpdate,
  onAdminToggle,
  // Debug props for sales/views overlay
  sortBy,
  sortOrder,
  // Priority loading for deep links
  priorityListingId = null
}) => {
  // Debug: log incoming props that affect layout
  useEffect(() => {
    if (DEBUG_STICKER_GRID) {
      console.log('🎨 StickerGrid props:', {
        groupByCategory,
        listings: listings?.length || 0,
        loading,
        error: !!error,
        sortBy,
        sortOrder
      });
    }
  }, [groupByCategory, listings, loading, error, sortBy, sortOrder]);

  // Force simple grid when modal is open regardless of groupByCategory
  const shouldGroup = isModalOpen ? false : groupByCategory;
  // State for category scenes
  const [categoryScenes, setCategoryScenes] = useState({});

  // State for listing-based scene insertions
  const [listingSceneInsertions, setListingSceneInsertions] = useState([]);

  // Hide debug overlay on homepage for regular users
  const showDebugOverlay = false; // Disabled for public homepage
  const debugType = sortBy === 'views' ? 'views' : 'sales';

  // Group listings by category if enabled
  const groupedListings = useMemo(() => {
    if (!groupByCategory) return {};

    const grouped = {};
    listings.forEach(listing => {
      const category = listing.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(listing);
    });

    // Custom sort order for categories - dinosaurs first, then by count (most first)
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      // Always put dinosaurs first (check for various dinosaur category names)
      const aIsDino = a.toLowerCase().includes('dinosaur') || a.toLowerCase().includes('dino');
      const bIsDino = b.toLowerCase().includes('dinosaur') || b.toLowerCase().includes('dino');
      
      if (aIsDino && !bIsDino) return -1;
      if (!aIsDino && bIsDino) return 1;
      
      // For all other categories, sort by count (most first)
      const aCount = grouped[a].length;
      const bCount = grouped[b].length;
      
      if (aCount !== bCount) {
        return bCount - aCount; // Most first
      }
      
      // If counts are equal, sort alphabetically
      return a.localeCompare(b);
    });

    // Create new object with sorted categories
    const sortedGrouped = {};
    sortedCategories.forEach(category => {
      sortedGrouped[category] = grouped[category];
    });

    return sortedGrouped;
  }, [listings, groupByCategory]);

  // Load listing scene insertions
  useEffect(() => {
    const loadListingSceneInsertions = async () => {
      try {
        const insertions = await parallaxService.getAllListingSceneInsertions();
        setListingSceneInsertions(insertions);
      } catch (error) {
        console.error('❌ Error loading listing scene insertions:', error);
      }
    };

    loadListingSceneInsertions();
  }, []);

  // Helper function to render listings with scene insertions
  const renderListingsWithInsertions = useCallback((categoryListings, category) => {
    const elements = [];
    
    // Debug: Check for pack listings (removed to reduce console noise)
    
    // Create a map of listing insertions for this category
    const insertionMap = new Map();
    listingSceneInsertions.forEach(insertion => {
      // Find the listing in the current category
      const targetListing = categoryListings.find(listing => listing.etsyListingId === insertion.listing_id);
      if (targetListing) {
        insertionMap.set(insertion.listing_id, insertion.scene_name);
      }
    });

    // Render each listing, inserting scenes before target listings
    categoryListings.forEach((listing, index) => {
      const listingId = listing.etsyListingId;
      
      // Check if this listing has a scene insertion
      if (insertionMap.has(listingId)) {
        const sceneName = insertionMap.get(listingId);
        
        // Insert the parallax scene BEFORE this listing
        elements.push(
          <div key={`scene-insertion-${listingId}`} className="category-parallax-scene" style={{ gridColumn: '1 / -1', marginBottom: '20px' }}>
            <ParallaxComponent
              category={category.toLowerCase()}
              availableSizes={['ATOM', 'PIXEL', 'NANO', 'MICRO', 'SMALL', 'MEDIUM']}
              height="60vh"
              instanceId={`listing-insertion-${listingId}`}
              loadSceneName={sceneName}
              sceneStickers={[]}
              onStickerClick={onStickerClick}
              onSceneStickersUpdate={onSceneStickersUpdate}
              onSceneNameUpdate={onSceneNameUpdate}
              showAdmin={false}
              onAdminToggle={onAdminToggle}
            />
          </div>
        );
      }

      // Add the actual listing
      elements.push(
        <div 
          key={listing.etsyListingId}
          className="sticker-card-wrapper"
          onClick={() => onCardClick(listing)}
        >
          <HomepageStickerCard
            listingId={listing.etsyListingId}
            title={listing.title}
            category={listing.category}
            firstImageUrl={listing.first_image_url}
            largestSizeImageUrl={listing.largest_size_image_url}
            etsyUrl={listing.etsy_url}
            onCardClick={() => onCardClick(listing)}
            onAddToScene={onAddToScene}
            totalSalesVolume={listing.total_sales_volume}
            views={listing.views}
            showDebugOverlay={showDebugOverlay}
            debugType={debugType}
            // Pack data
            svgPath={listing.svgPath}
            svgPaths={listing.svgPaths}
            svgTitles={listing.svgTitles}
            isSet={listing.isSet}
            packType={listing.packType}
            setQuantity={listing.setQuantity}
            // TRUEscale
            isTrueScale={listing.isTrueScale === 1 || listing.isTrueScale === true}
            // Priority loading for deep links
            isPriorityLoad={priorityListingId === listing.etsyListingId}
            // Modal state - pause loading until modal image loads
            isModalOpen={isModalOpen}
            modalImageLoaded={modalImageLoaded}
          />
        </div>
      );
    });

    return elements;
  }, [listingSceneInsertions, onCardClick, onStickerClick, onSceneStickersUpdate, onSceneNameUpdate, onAdminToggle, onAddToScene, showDebugOverlay, debugType, priorityListingId]);

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <ClothedEyeLoader 
          size="large" 
          showTagline={true}
          fullscreen={true}
          message="Loading all the decals..."
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <ClothedEyeError
        errorType="generic"
        title="Failed to Display Stickers"
        description={error || "An unexpected error occurred while loading the sticker grid. Please try again."}
        onRetry={() => window.location.reload()}
        onGoHome={() => window.location.href = '/'}
        fullscreen={false}
        size="medium"
        theme="dark"
      />
    );
  }

  return (
    <main className="new-homepage-main" ref={containerRef}>
      {listings.length === 0 && !loading && !error ? (
        <div className="no-results">
          <p>No stickers found. Try adjusting your search or filters.</p>
        </div>
      ) : shouldGroup ? (
        // Grouped display
        <div className="category-groups">
          {Object.entries(groupedListings).map(([category, categoryListings]) => {
            const assignedScene = categorySceneAssignments[category];
            
            return (
              <div key={category} className="category-group">
                {/* Category Scene - Show if assigned */}
                {assignedScene && (
                  <div className="category-parallax-scene">
                    <ParallaxComponent
                      category={category.toLowerCase()}
                      availableSizes={['ATOM', 'PIXEL', 'NANO', 'MICRO', 'SMALL', 'MEDIUM']}
                      height="60vh"
                      instanceId={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                      loadSceneName={assignedScene}
                      sceneStickers={categoryScenes[category] || []}
                      onStickerClick={onStickerClick}
                      onSceneStickersUpdate={(stickers) => {
                        setCategoryScenes(prev => ({
                          ...prev,
                          [category]: stickers
                        }));
                        if (onSceneStickersUpdate) {
                          onSceneStickersUpdate(stickers);
                        }
                      }}
                      onSceneNameUpdate={(sceneName) => {
                        if (onSceneNameUpdate) {
                          onSceneNameUpdate(sceneName);
                        }
                      }}
                      showAdmin={false}
                      onAdminToggle={onAdminToggle}
                    />
                  </div>
                )}
                
                {showCategoryHeaders && (
                  <div className="category-header">
                    <h2 className="category-title">{category}</h2>
                    <span className="category-count">{categoryListings.length} stickers</span>
                  </div>
                )}
                <div className="homepage-large-grid">
                  {renderListingsWithInsertions(categoryListings, category)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Regular grid display
        <div className="homepage-large-grid">
          {listings.map((listing) => (
            <div 
              key={listing.etsyListingId}
              className="sticker-card-wrapper"
              onClick={() => onCardClick(listing)}
            >
              <HomepageStickerCard
                listingId={listing.etsyListingId}
                title={listing.title}
                category={listing.category}
                firstImageUrl={listing.first_image_url}
                largestSizeImageUrl={listing.largest_size_image_url}
                etsyUrl={listing.etsy_url}
                onCardClick={() => onCardClick(listing)}
                onAddToScene={onAddToScene}
                totalSalesVolume={listing.total_sales_volume}
                views={listing.views}
                showDebugOverlay={showDebugOverlay}
                debugType={debugType}
                isTrueScale={listing.isTrueScale === 1 || listing.isTrueScale === true}
                isPriorityLoad={priorityListingId === listing.etsyListingId}
                // Modal state - pause loading until modal image loads
                isModalOpen={isModalOpen}
                modalImageLoaded={modalImageLoaded}
              />
            </div>
          ))}
        </div>
      )}

      {/* End of Results */}
      {!listings.length && (
        <div className="end-of-results">
          <p>No stickers found matching your criteria.</p>
        </div>
      )}

    </main>
  );
});

StickerGrid.propTypes = {
  listings: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  error: PropTypes.string,
  groupByCategory: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  modalImageLoaded: PropTypes.bool,
  containerRef: PropTypes.object,
  onCardClick: PropTypes.func,
  onAddToScene: PropTypes.func,
  showCategoryHeaders: PropTypes.bool,
  categorySceneAssignments: PropTypes.object,
  onStickerClick: PropTypes.func,
  onSceneStickersUpdate: PropTypes.func,
  onSceneNameUpdate: PropTypes.func,
  onAdminToggle: PropTypes.func,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string,
  priorityListingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default StickerGrid; 